import { useState, useCallback, useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics";
import SidebarLayout from "@/components/SidebarLayout";
import QuickSetup from "@/components/onboarding/QuickSetup";
import OnboardingFlow from "@/components/onboarding/OnboardingFlow";
import Dashboard from "@/components/dashboard/Dashboard";
import SplashPage from "@/pages/SplashPage";
import AuthPage from "@/pages/AuthPage";
import UsageBanner from "@/components/UsageBanner";
import { useAuth } from "@/hooks/useAuth";
import { useProfileSync } from "@/hooks/useProfileSync";
import { loadState, saveState, AppState, UserProfile } from "@/lib/storage";
import {
  loadUsage,
  markSetupComplete,
  getNudgeInfo,
  AccessTier,
} from "@/lib/usage";
import { Loader2 } from "lucide-react";

type Screen = "splash" | "setup" | "auth" | "dashboard" | "enrich";

export default function Index() {
  const { user, loading: authLoading } = useAuth();
  const { state, updateState, loading: profileLoading } = useProfileSync();

  // Local state for unauthenticated users
  const [localState, setLocalState] = useState<AppState | null>(null);
  const [screen, setScreen] = useState<Screen>("splash");
  const [editingProfile, setEditingProfile] = useState(false);
  const [usageTier, setUsageTier] = useState<AccessTier>("free");
  const [nudgeInfo, setNudgeInfo] = useState({
    tier: "free" as AccessTier,
    remainingUses: 0,
    hoursLeft: 24,
  });
  const [authInitialMode, setAuthInitialMode] = useState<"signin" | "signup">("signup");
  const [authReturnScreen, setAuthReturnScreen] = useState<Screen>("dashboard");

  const trialExpiredTracked = useRef(false);

  useEffect(() => {
    if (usageTier === "nudge" && !trialExpiredTracked.current) {
      trialExpiredTracked.current = true;
      trackEvent("trial_expired");
    }
  }, [usageTier]);

  const openAuth = useCallback((mode: "signin" | "signup", returnScreen: Screen = "dashboard") => {
    setAuthInitialMode(mode);
    setAuthReturnScreen(returnScreen);
    setScreen("auth");
  }, []);

  // Load local state for unauthenticated users
  useEffect(() => {
    if (!user && !authLoading) {
      const saved = loadState();
      setLocalState(saved);

      const usage = loadUsage();
      const info = getNudgeInfo(usage);
      setNudgeInfo(info);
      setUsageTier(info.tier);

      // If they've completed setup before, go straight to dashboard
      if (usage.setupComplete) {
        setScreen("dashboard");
      }
    }
  }, [user, authLoading]);

  // Refresh nudge info periodically (every 60s)
  useEffect(() => {
    if (user) return;
    const interval = setInterval(() => {
      const usage = loadUsage();
      const info = getNudgeInfo(usage);
      setNudgeInfo(info);
      setUsageTier(info.tier);
    }, 60_000);
    return () => clearInterval(interval);
  }, [user]);

  // Local state updater for unauthenticated users
  const updateLocalState = useCallback(
    (newState: AppState | ((prev: AppState) => AppState)) => {
      setLocalState((prev) => {
        const current = prev || loadState();
        const next = typeof newState === "function" ? newState(current) : newState;
        saveState(next);
        return next;
      });
    },
    []
  );

  // Handle quick setup completion
  const handleSetupComplete = () => {
    markSetupComplete();
    setScreen("dashboard");
  };

  // Handle profile update from quick setup (local only, no DB)
  const handleLocalProfileUpdate = (profile: UserProfile) => {
    updateLocalState((prev) => ({ ...prev, profile }));
  };

  // Show loading while auth initializes
  if (authLoading || (user && profileLoading)) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </SidebarLayout>
    );
  }

  // ──────────────────────────────────
  // AUTHENTICATED USER FLOW
  // ──────────────────────────────────
  if (user) {
    // Signed in but hasn't gone through profile enrichment yet, or editing
    if (!state.onboarded || editingProfile) {
      return (
        <SidebarLayout>
          <div className="flex-1 overflow-y-auto">
            <OnboardingFlow
              profile={state.profile}
              onUpdate={(profile) => updateState((prev) => ({ ...prev, profile }))}
              onComplete={() => {
                setEditingProfile(false);
                updateState((prev) => ({ ...prev, onboarded: true }));
              }}
            />
          </div>
        </SidebarLayout>
      );
    }

    // Signed in and onboarded: full dashboard
    return (
      <SidebarLayout showAutoFill>
        <Dashboard
          state={state}
          onUpdate={updateState}
          onEditProfile={() => setEditingProfile(true)}
          isAuthenticated
        />
      </SidebarLayout>
    );
  }

  // ──────────────────────────────────
  // UNAUTHENTICATED USER FLOW
  // ──────────────────────────────────
  const currentState = localState || loadState();

  // Splash screen
  if (screen === "splash") {
    return (
      <SidebarLayout>
        <div className="flex-1 overflow-y-auto">
          <SplashPage onGetStarted={() => setScreen("setup")} />
        </div>
      </SidebarLayout>
    );
  }

  // Auth page (sign in / sign up)
  if (screen === "auth") {
    return (
      <SidebarLayout>
        <div className="flex-1 overflow-y-auto">
          <AuthPage
            onBack={() => setScreen(authReturnScreen)}
            initialAuthMode={authInitialMode}
          />
        </div>
      </SidebarLayout>
    );
  }

  // Quick setup (replaces the old 6-step onboarding for new users)
  if (screen === "setup") {
    return (
      <SidebarLayout>
        <div className="flex-1 overflow-y-auto">
          <QuickSetup
            profile={currentState.profile}
            onChange={handleLocalProfileUpdate}
            onComplete={handleSetupComplete}
            onJumpToSignIn={() => openAuth("signin", "setup")}
          />
        </div>
      </SidebarLayout>
    );
  }

  // Dashboard for unauthenticated users (limited by usage tier)
  if (screen === "dashboard") {
    // Hard locked — force auth
    if (usageTier === "locked") {
      return (
        <SidebarLayout>
          <UsageBanner
            tier="locked"
            hoursLeft={0}
            remainingUses={0}
            onSignUp={() => openAuth("signup")}
          />
          <div className="flex-1 overflow-y-auto opacity-50 pointer-events-none">
            <Dashboard
              state={currentState}
              onUpdate={updateLocalState}
              onEditProfile={() => {}}
              onSignUp={() => openAuth("signup")}
              isAuthenticated={false}
            />
          </div>
        </SidebarLayout>
      );
    }

    return (
      <SidebarLayout showAutoFill>
        <UsageBanner
          tier={nudgeInfo.tier}
          hoursLeft={nudgeInfo.hoursLeft}
          remainingUses={nudgeInfo.remainingUses}
          onSignUp={() => openAuth("signup")}
        />
        <Dashboard
          state={currentState}
          onUpdate={updateLocalState}
          onEditProfile={() => setScreen("setup")}
          onSignUp={() => openAuth("signup")}
          isAuthenticated={false}
        />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="flex items-center justify-center h-full p-6 text-center text-sm text-muted-foreground">
        Something went wrong loading this screen. Try refreshing the page.
      </div>
    </SidebarLayout>
  );
}
